import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hrAPI, hrSettingsAPI, normalizeList, normalizeError } from '../../lib/api';
import { COUNTRIES, STATES_BY_COUNTRY } from '../../lib/locationData';
import { usePermissions } from '../../contexts/PermissionContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Checkbox } from '../../components/ui/checkbox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { Plus, Edit, Trash2, Search, RefreshCw, Info, Power, User, MapPin, Briefcase, Clock, DollarSign, Shield, Loader2 } from 'lucide-react';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';

// Fixed departments (CRM-only)
const DEPARTMENTS = [
  { value: 'sales', label: 'Sales' },
  { value: 'client', label: 'Client' },
  { value: 'account', label: 'Account' },
];

// Fixed roles
const ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'team_lead', label: 'Team Lead' },
  { value: 'user', label: 'User' }
];

// Product roles (removed 'client')
const PRODUCT_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'manage', label: 'Manage' },
  { value: 'user', label: 'User' }
];

// Countries (sample list)
// Department to module mapping
const DEPARTMENT_MODULES = {
  'sales': 'lead',
  'client': 'client',
  'account': 'account',
};

export default function Users() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState({});

  // One-click permission presets — pre-fill the matrix for common roles.
  const ROLE_TEMPLATES = {
    'Sales Rep': (mod) => ['lead', 'client', 'outreach', 'schedule'].includes(mod)
      ? { create: true, view: true, update: true, delete: false }
      : mod === 'report' ? { create: false, view: true, update: false, delete: false }
      : { create: false, view: false, update: false, delete: false },
    'Manager': (mod) => mod === 'billing'
      ? { create: false, view: true, update: false, delete: false }
      : { create: true, view: true, update: true, delete: true },
    'Finance': (mod) => ['billing', 'invoice', 'payment', 'quote'].includes(mod)
      ? { create: true, view: true, update: true, delete: false }
      : mod === 'report' ? { create: false, view: true, update: false, delete: false }
      : ['client'].includes(mod) ? { create: false, view: true, update: false, delete: false }
      : { create: false, view: false, update: false, delete: false },
    'Read Only': () => ({ create: false, view: true, update: false, delete: false }),
  };

  const applyRoleTemplate = (templateName) => {
    const rule = ROLE_TEMPLATES[templateName];
    const perms = {};
    Object.keys(modules).forEach((moduleKey) => {
      perms[moduleKey] = {};
      (modules[moduleKey].submodules || []).forEach((sub) => {
        perms[moduleKey][sub] = { ...rule(moduleKey) };
      });
    });
    setFormData((prev) => ({ ...prev, module_permissions: perms }));
    toast.success(`${templateName} template applied — adjust checkboxes as needed`);
  };
  const [loading, setLoading] = useState(true);
  
  // Action loading states (Button State Contract)
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // HR Settings data
  const [hrWorkingTimes, setHrWorkingTimes] = useState([]);
  const [hrWorkingDays, setHrWorkingDays] = useState([]);
  const [hrDepartments, setHrDepartments] = useState([]);
  const [hrPositions, setHrPositions] = useState([]);
  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [editSheetOpen, setEditSheetOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [pendingEditUserId, setPendingEditUserId] = useState(null);
  
  // Quick Invite state
  const [quickInviteOpen, setQuickInviteOpen] = useState(false);
  const [isInviting, setIsInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', department: 'sales', role: 'user' });
  const [invitePassword, setInvitePassword] = useState('');
  const [showInvitePassword, setShowInvitePassword] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Form state - permissions organized by department
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    department: 'sales',
    role: '',
    address: '',
    country: '',
    state: '',
    city: '',
    zipcode: '',
    working_time: '',
    working_time_id: '',
    working_days_id: '',
    hr_department_id: '',
    position_id: '',
    salary_info: {
      salary_type: 'monthly',
      base_salary: ''
    },
    track_user_logs: false,
    module_permissions: {},
    show_admin_data: false,
    employee_id: '',
    join_date: ''
  });

  // Department permissions state (role, access, sad for each department)
  const [departmentPermissions, setDepartmentPermissions] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  // Handle edit query parameter from UserDetails page
  useEffect(() => {
    const editUserId = searchParams.get('edit');
    if (editUserId && users.length > 0 && !loading) {
      const userToEdit = users.find(u => u.id === editUserId);
      if (userToEdit) {
        openEditSheet(userToEdit);
        // Clear the query parameter after opening the sheet
        setSearchParams({});
      }
    }
  }, [searchParams, users, loading]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Use allSettled so individual API failures don't block the whole page
      const [usersRes, permsRes, deptRes, posRes] = await Promise.allSettled([
        hrAPI.getUsers(),
        hrAPI.getPermissions(),
        hrSettingsAPI.getDepartments(),
        hrSettingsAPI.getPositions()
      ]);
      
      // Users and Permissions are critical — show error only if THESE fail
      if (usersRes.status === 'rejected') {
        console.error('Failed to load users:', usersRes.reason);
        toast.error(usersRes.reason?.response?.data?.detail || 'Failed to load users');
      } else {
        setUsers(normalizeList(usersRes.value.data, 'users'));
      }
      
      const permsData = permsRes.status === 'fulfilled'
        ? ((permsRes.value.data && permsRes.value.data.modules) || permsRes.value.data || {})
        : {};
      setModules(permsData);
      
      // Departments and Positions are optional — degrade gracefully
      setHrWorkingTimes([]);
      setHrWorkingDays([]);
      setHrDepartments(deptRes.status === 'fulfilled' ? normalizeList(deptRes.value.data) : []);
      setHrPositions(posRes.status === 'fulfilled' ? normalizeList(posRes.value.data) : []);
      
      // Initialize permissions structure
      const initialPerms = {};
      Object.keys(permsData).forEach(moduleKey => {
        initialPerms[moduleKey] = {};
        permsData[moduleKey].submodules.forEach(submodule => {
          initialPerms[moduleKey][submodule] = {
            create: false,
            view: false,
            update: false,
            delete: false
          };
        });
      });
      setFormData(prev => ({ ...prev, module_permissions: initialPerms }));
      
      // Initialize department permissions (role, access, sad for each department)
      const initialDeptPerms = {};
      DEPARTMENTS.forEach(dept => {
        initialDeptPerms[dept.value] = {
          role: 'user',  // default role per department
          access: false,
          sad: false
        };
      });
      setDepartmentPermissions(initialDeptPerms);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    // Button State Contract: Disable button while loading
    if (isCreating) return;
    setIsCreating(true);
    
    // Validation
    if (!formData.name || !formData.email) {
      toast.error('Name and email are required');
      setIsCreating(false);
      return;
    }
    if (!formData.role) {
      toast.error('Role is required');
      setIsCreating(false);
      return;
    }

    try {
      // Clean empty strings to null for optional fields
      const cleaned = { ...formData };
      ['join_date', 'hr_department_id', 'position_id', 'working_time_id', 'working_days_id', 'zipcode', 'address', 'employee_id'].forEach(k => {
        if (cleaned[k] === '') cleaned[k] = null;
      });
      const submitData = {
        ...cleaned,
        salary_info: formData.salary_info?.base_salary ? {
          salary_type: formData.salary_info.salary_type,
          base_salary: parseFloat(formData.salary_info.base_salary)
        } : null,
        generate_password: true,
        send_email: true
      };
      
      const response = await hrAPI.createUser(submitData);
      
      // Check if email was sent successfully
      if (response.data.email_sent) {
        toast.success('User created successfully. Password sent via email.');
      } else {
        // Email failed - show password in modal
        const password = response.data.temp_password;
        toast.warning(
          `User created but email failed. Password: ${password}`,
          {
            duration: 30000,  // Show for 30 seconds
            description: `Email: ${formData.email} - Please save this password and share it manually.`
          }
        );
        
        // Also log to console for easy copying
        console.log('=== USER CREDENTIALS ===');
        console.log('Email:', formData.email);
        console.log('Password:', password);
        console.log('========================');
      }
      
      setCreateSheetOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(normalizeError(error, 'Failed to create user'));
    } finally {
      // Button State Contract: Re-enable button
      setIsCreating(false);
    }
  };

  // Quick Invite Handler - Simplified user invitation
  const handleQuickInvite = async () => {
    if (!inviteForm.name || !inviteForm.email) {
      toast.error('Name and email are required');
      return;
    }
    if (!inviteForm.role) {
      toast.error('Role is required');
      return;
    }
    
    setIsInviting(true);
    try {
      // Use defaults for required fields to simplify invite flow
      const defaultWorkingTime = hrWorkingTimes[0]?.id || '';
      const defaultWorkingDays = hrWorkingDays[0]?.id || '';
      
      const submitData = {
        name: inviteForm.name,
        email: inviteForm.email,
        phone: '',
        department: inviteForm.department,
        role: inviteForm.role,
        address: '',
        country: 'US',
        state: 'California',
        city: 'San Francisco',
        zipcode: '',
        working_time_id: defaultWorkingTime,
        working_days_id: defaultWorkingDays,
        hr_department_id: '',
        position_id: '',
        salary_info: null,
        track_user_logs: false,
        module_permissions: {},
        show_admin_data: false,
        employee_id: '',
        join_date: null,
        generate_password: true,
        send_email: true
      };
      
      const response = await hrAPI.createUser(submitData);
      
      if (response.data.email_sent) {
        toast.success('Invitation sent! User will receive their login credentials via email.');
        setQuickInviteOpen(false);
        setInviteForm({ name: '', email: '', department: '', role: 'user' });
      } else {
        // Email failed - show password dialog
        setInvitePassword(response.data.temp_password);
        setShowInvitePassword(true);
        toast.warning('User created but email delivery failed. Please share the password manually.');
      }
      
      loadData();
    } catch (error) {
      console.error('Failed to invite user:', error);
      toast.error(normalizeError(error, 'Failed to invite user'));
    } finally {
      setIsInviting(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    // Button State Contract: Disable button while loading
    if (isUpdating) return;
    setIsUpdating(true);

    try {
      // Prepare department permissions in correct format
      const formattedDeptPerms = {};
      Object.keys(departmentPermissions).forEach(dept => {
        if (departmentPermissions[dept].access) {
          formattedDeptPerms[dept] = {
            role: departmentPermissions[dept].role,
            access: true,
            show_admin_data: departmentPermissions[dept].sad || false
          };
        }
      });

      // Clean empty strings to null for optional fields
      const cleaned = { ...formData };
      ['join_date', 'hr_department_id', 'position_id', 'working_time_id', 'working_days_id', 'zipcode', 'address', 'employee_id'].forEach(k => {
        if (cleaned[k] === '') cleaned[k] = null;
      });

      const submitData = {
        ...cleaned,
        department_permissions: formattedDeptPerms,
        module_permissions: formData.module_permissions,
        salary_info: formData.salary_info?.base_salary ? {
          salary_type: formData.salary_info.salary_type,
          base_salary: parseFloat(formData.salary_info.base_salary)
        } : null
      };
      
      await hrAPI.updateUser(selectedUser.id, submitData);
      toast.success('User updated successfully');
      setEditSheetOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(normalizeError(error, 'Failed to update user'));
    } finally {
      // Button State Contract: Re-enable button
      setIsUpdating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      await hrAPI.deleteUser(selectedUser.id);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      loadData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(normalizeError(error, 'Failed to delete user'));
    }
  };

  const handleResetPassword = async (userId) => {
    try {
      const response = await hrAPI.resetPassword(userId, { user_id: userId, send_email: true });
      
      // Check if email was sent successfully
      if (response.data.email_sent) {
        toast.success('Password reset email sent successfully');
      } else {
        // Email failed - show password
        const password = response.data.temp_password;
        const userEmail = response.data.user_email;
        const userName = response.data.user_name;
        
        toast.warning(
          `Password reset but email failed. New Password: ${password}`,
          {
            duration: 30000,  // Show for 30 seconds
            description: `User: ${userName} (${userEmail}) - Please save and share this password manually.`
          }
        );
        
        // Log to console
        console.log('=== PASSWORD RESET ===');
        console.log('User:', userName);
        console.log('Email:', userEmail);
        console.log('New Password:', password);
        console.log('======================');
      }
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error('Failed to reset password');
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const newStatus = !user.is_active;
      await hrAPI.updateUser(user.id, { is_active: newStatus });
      toast.success(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
      loadData();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const openEditSheet = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.full_name || user.name || '',
      email: user.email,
      phone: user.phone || '',
      department: user.department || '',
      role: user.role || '',
      address: user.address || '',
      country: user.country || '',
      state: user.state || '',
      city: user.city || '',
      zipcode: user.zipcode || '',
      working_time: user.working_time || '',
      working_time_id: user.working_time_id || '',
      working_days_id: user.working_days_id || '',
      hr_department_id: user.hr_department_id || '',
      position_id: user.position_id || '',
      salary_info: user.salary_info || { salary_type: 'monthly', base_salary: '' },
      track_user_logs: user.track_user_logs || false,
      module_permissions: user.module_permissions || {},
      department_permissions: user.department_permissions || {},
      employee_id: user.employee_id || '',
      join_date: user.join_date || ''
    });
    
    // Load department permissions from user data
    const deptPerms = {};
    DEPARTMENTS.forEach(dept => {
      if (user.department_permissions && user.department_permissions[dept.value]) {
        deptPerms[dept.value] = {
          role: user.department_permissions[dept.value].role || 'user',
          access: user.department_permissions[dept.value].access || false,
          sad: user.department_permissions[dept.value].show_admin_data || false
        };
      } else {
        deptPerms[dept.value] = {
          role: 'user',
          access: false,
          sad: false
        };
      }
    });
    setDepartmentPermissions(deptPerms);
    
    setEditSheetOpen(true);
  };

  const openDeleteDialog = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const resetForm = () => {
    const initialPerms = {};
    Object.keys(modules).forEach(moduleKey => {
      initialPerms[moduleKey] = {};
      modules[moduleKey].submodules.forEach(submodule => {
        initialPerms[moduleKey][submodule] = {
          create: false,
          view: false,
          update: false,
          delete: false
        };
      });
    });
    
    const initialDeptPerms = {};
    DEPARTMENTS.forEach(dept => {
      initialDeptPerms[dept.value] = {
        role: 'user',
        access: false,
        sad: false
      };
    });
    
    setFormData({
      name: '',
      email: '',
      phone: '',
      department: '',
      role: '',
      address: '',
      country: '',
      state: '',
      city: '',
      zipcode: '',
      working_time: '',
      working_time_id: '',
      working_days_id: '',
      hr_department_id: '',
      position_id: '',
      salary_info: { salary_type: 'monthly', base_salary: '' },
      track_user_logs: false,
      module_permissions: initialPerms,
      show_admin_data: false,
      employee_id: '',
      join_date: ''
    });
    setDepartmentPermissions(initialDeptPerms);
    setSelectedUser(null);
  };

  const togglePermission = (moduleKey, submodule, action) => {
    setFormData(prev => ({
      ...prev,
      module_permissions: {
        ...prev.module_permissions,
        [moduleKey]: {
          ...prev.module_permissions[moduleKey],
          [submodule]: {
            ...prev.module_permissions[moduleKey][submodule],
            [action]: !prev.module_permissions[moduleKey][submodule][action]
          }
        }
      }
    }));
  };

  const toggleDepartmentAccess = (deptValue) => {
    const newAccessState = !departmentPermissions[deptValue]?.access;
    
    // Update access state
    setDepartmentPermissions(prev => ({
      ...prev,
      [deptValue]: {
        ...prev[deptValue],
        access: newAccessState
      }
    }));
    
    // Apply permissions based on access state and current role
    if (newAccessState) {
      // Access enabled - apply role-based permissions
      const role = departmentPermissions[deptValue]?.role || 'user';
      applyDepartmentRolePermissions(deptValue, role);
    } else {
      // Access disabled - clear all permissions for this department
      clearDepartmentPermissions(deptValue);
    }
  };

  const toggleDepartmentSAD = (deptValue) => {
    setDepartmentPermissions(prev => ({
      ...prev,
      [deptValue]: {
        ...prev[deptValue],
        sad: !prev[deptValue].sad
      }
    }));
  };

  // Change role for a specific department
  const changeDepartmentRole = (deptValue, role) => {
    // Update role in department permissions
    setDepartmentPermissions(prev => ({
      ...prev,
      [deptValue]: {
        ...prev[deptValue],
        role: role
      }
    }));
    
    // If access is enabled, apply new role permissions
    if (departmentPermissions[deptValue]?.access) {
      applyDepartmentRolePermissions(deptValue, role);
    }
  };

  // Apply permissions based on role for a specific department
  const applyDepartmentRolePermissions = (deptValue, role) => {
    const moduleKey = DEPARTMENT_MODULES[deptValue];
    if (!moduleKey || !modules[moduleKey]) return;
    
    // Define what permissions each role gets
    const rolePermissions = {
      'admin': { create: true, view: true, update: true, delete: true },    // CRUD
      'manage': { create: true, view: true, update: true, delete: false },  // CRU
      'user': { create: true, view: true, update: false, delete: false }    // CR
    };
    
    const permissions = rolePermissions[role];
    if (!permissions) return;
    
    const updatedPermissions = { ...formData.module_permissions };
    
    // Apply to this department's module and submodules
    if (!updatedPermissions[moduleKey]) {
      updatedPermissions[moduleKey] = {};
    }
    
    modules[moduleKey].submodules.forEach(submodule => {
      updatedPermissions[moduleKey][submodule] = { ...permissions };
    });
    
    setFormData(prev => ({
      ...prev,
      module_permissions: updatedPermissions
    }));
  };

  // Clear permissions for a specific department
  const clearDepartmentPermissions = (deptValue) => {
    const moduleKey = DEPARTMENT_MODULES[deptValue];
    if (!moduleKey || !modules[moduleKey]) return;
    
    const updatedPermissions = { ...formData.module_permissions };
    
    // Clear all permissions for this department's module
    if (!updatedPermissions[moduleKey]) {
      updatedPermissions[moduleKey] = {};
    }
    
    modules[moduleKey].submodules.forEach(submodule => {
      updatedPermissions[moduleKey][submodule] = {
        create: false,
        view: false,
        update: false,
        delete: false
      };
    });
    
    setFormData(prev => ({
      ...prev,
      module_permissions: updatedPermissions
    }));
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.employee_id && user.employee_id.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesDepartment = departmentFilter === 'all' || user.department === departmentFilter;
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);

    return matchesSearch && matchesDepartment && matchesRole && matchesStatus;
  });

  const renderUserForm = (isEdit = false) => (
    <div className="space-y-4 mt-4">
      {/* Personal Information + Role */}
      <div className="rounded-lg border bg-card p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="p-2 rounded-md bg-primary/10">
            <User className="h-4 w-4 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">User Information</h3>
            <p className="text-xs text-muted-foreground">Basic details and access control</p>
          </div>
        </div>
        <Separator className="mb-3" />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Full Name <span className="text-destructive">*</span></Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="John Doe" data-testid="user-name-input" className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-xs">Email <span className="text-destructive">*</span></Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="john@example.com" disabled={isEdit} data-testid="user-email-input" className="mt-1 h-9" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Phone</Label>
              <Input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1 234 567 8900" className="mt-1 h-9" />
            </div>
            <div>
              <Label className="text-xs">Employee ID</Label>
              <Input value={formData.employee_id} onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })} placeholder="EMP001" className="mt-1 h-9" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">System Role <span className="text-destructive">*</span></Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger data-testid="role-select" className="mt-1 h-9"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{ROLES.map(role => (<SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Global Show Admin Data Toggle */}
      <div className="border rounded-lg p-4 bg-muted/30">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <Label className="text-base font-semibold flex items-center gap-2">
              Show Admin Data (SAD)
              <Info className="h-4 w-4 text-muted-foreground" />
            </Label>
            <p className="text-sm text-muted-foreground">
              When enabled, this user can view admin-created data across all modules
            </p>
          </div>
          <Checkbox
            checked={formData.show_admin_data || false}
            onCheckedChange={(checked) => setFormData({ ...formData, show_admin_data: checked })}
            data-testid="show-admin-data-checkbox"
          />
        </div>
      </div>

      {/* Permissions & Access - New Design */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Permissions & Access</h3>
        {/* Role templates: one-click permission presets */}
        <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="role-templates-row">
          <span className="text-xs text-muted-foreground">Quick templates:</span>
          {Object.keys(ROLE_TEMPLATES).map((t) => (
            <Button key={t} type="button" size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => applyRoleTemplate(t)} data-testid={`role-template-${t.toLowerCase().replace(/\s/g, '-')}`}>
              {t}
            </Button>
          ))}
        </div>
        <div className="space-y-6">
          {DEPARTMENTS.map((dept) => {
            const moduleKey = DEPARTMENT_MODULES[dept.value];
            const moduleData = modules[moduleKey];
            
            if (!moduleData) return null;
            
            return (
              <div key={dept.value} className="border rounded-lg p-4 space-y-4">
                {/* Department Header */}
                <div className="flex items-center justify-between pb-4 border-b">
                  <div className="flex items-center gap-4">
                    <div className="font-semibold text-base capitalize">{dept.label}</div>
                    <div className="flex items-center gap-2">
                      <Label className="text-sm text-muted-foreground">Role:</Label>
                      <Select 
                        value={departmentPermissions[dept.value]?.role || 'user'} 
                        onValueChange={(role) => changeDepartmentRole(dept.value, role)}
                      >
                        <SelectTrigger className="w-[120px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRODUCT_ROLES.map(role => (
                            <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <Label className="text-sm">Access</Label>
                      <Checkbox
                        checked={departmentPermissions[dept.value]?.access || false}
                        onCheckedChange={() => toggleDepartmentAccess(dept.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Module Permissions Table */}
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Module</TableHead>
                        <TableHead className="text-center w-[80px]">Create</TableHead>
                        <TableHead className="text-center w-[80px]">View</TableHead>
                        <TableHead className="text-center w-[80px]">Update</TableHead>
                        <TableHead className="text-center w-[80px]">Delete</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {moduleData.submodules.map((submodule) => (
                        <TableRow key={submodule}>
                          <TableCell className="capitalize">{submodule.replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={formData.module_permissions[moduleKey]?.[submodule]?.create || false}
                                onCheckedChange={() => togglePermission(moduleKey, submodule, 'create')}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={formData.module_permissions[moduleKey]?.[submodule]?.view || false}
                                onCheckedChange={() => togglePermission(moduleKey, submodule, 'view')}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={formData.module_permissions[moduleKey]?.[submodule]?.update || false}
                                onCheckedChange={() => togglePermission(moduleKey, submodule, 'update')}
                              />
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center">
                              <Checkbox
                                checked={formData.module_permissions[moduleKey]?.[submodule]?.delete || false}
                                onCheckedChange={() => togglePermission(moduleKey, submodule, 'delete')}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional Settings */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Additional Settings</h3>
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Switch
              checked={formData.track_user_logs}
              onCheckedChange={(checked) => setFormData({ ...formData, track_user_logs: checked })}
              id="track_logs"
            />
            <Label htmlFor="track_logs" className="cursor-pointer">
              Track User Logs
            </Label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={() => isEdit ? setEditSheetOpen(false) : setCreateSheetOpen(false)}>
          Cancel
        </Button>
        <Button 
          onClick={isEdit ? handleUpdateUser : handleCreateUser} 
          data-testid="submit-user-btn"
          disabled={isEdit ? isUpdating : isCreating}
        >
          {isEdit ? (
            isUpdating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )
          ) : (
            isCreating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              'Create User'
            )
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="p-6 space-y-6" data-testid="hr-users-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Users Management</h1>
          <p className="text-muted-foreground">Manage user accounts, departments, and permissions</p>
        </div>
        {hasPermission('hr', 'users', 'create') && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setQuickInviteOpen(true)} data-testid="quick-invite-btn">
              <User className="h-4 w-4 mr-2" />
              Quick Invite
            </Button>
            <Button onClick={() => { resetForm(); setCreateSheetOpen(true); }} data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or employee ID"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
        </div>
        <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Department" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Departments</SelectItem>
            {DEPARTMENTS.map(dept => (
              <SelectItem key={dept.value} value={dept.value}>{dept.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLES.map(role => (
              <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="icon" onClick={loadData} data-testid="refresh-btn">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      {/* Users Table */}
      <div className="border rounded-lg">
        {loading ? (
          <div className="p-8 space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      <button 
                        onClick={() => navigate(`/hr/users/${user.id}`)}
                        className="text-primary hover:underline cursor-pointer"
                      >
                        {user.name}
                      </button>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone || '-'}</TableCell>
                    <TableCell>{user.employee_id || '-'}</TableCell>
                    <TableCell className="capitalize">{user.department?.replace(/_/g, ' ') || '-'}</TableCell>
                    <TableCell className="capitalize">{user.role?.replace(/_/g, ' ') || '-'}</TableCell>
                    <TableCell>
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {hasPermission('hr', 'users', 'update') && (
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={(e) => { e.stopPropagation(); handleToggleActive(user); }} 
                            title={user.is_active ? 'Deactivate User' : 'Activate User'}
                            data-testid="toggle-active-btn"
                          >
                            <Power className={`h-4 w-4 ${user.is_active ? 'text-green-600' : 'text-gray-400'}`} />
                          </Button>
                        )}
                        {hasPermission('hr', 'users', 'update') && (
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEditSheet(user); }} data-testid="edit-user-btn">
                            <Edit className="h-4 w-4" />
                          </Button>
                        )}
                        {hasPermission('hr', 'users', 'update') && (
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); handleResetPassword(user.id); }} title="Reset Password">
                            <RefreshCw className="h-4 w-4" />
                          </Button>
                        )}
                        {hasPermission('hr', 'users', 'delete') && (
                          <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openDeleteDialog(user); }} data-testid="delete-user-btn">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Create User Sheet */}
      <Sheet open={createSheetOpen} onOpenChange={setCreateSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[700px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create New User</SheetTitle>
            <SheetDescription>
              Add a new user to your organization. Password will be automatically generated and sent via email.
            </SheetDescription>
          </SheetHeader>
          {renderUserForm(false)}
        </SheetContent>
      </Sheet>

      {/* Edit User Sheet */}
      <Sheet open={editSheetOpen} onOpenChange={setEditSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-[700px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit User</SheetTitle>
            <SheetDescription>
              Update user information and permissions
            </SheetDescription>
          </SheetHeader>
          {renderUserForm(true)}
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This will deactivate the user account. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700 text-white">
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Quick Invite Dialog */}
      <Sheet open={quickInviteOpen} onOpenChange={setQuickInviteOpen}>
        <SheetContent className="sm:max-w-md" data-testid="quick-invite-sheet">
          <SheetHeader>
            <SheetTitle>Quick Invite</SheetTitle>
            <SheetDescription>
              Send a quick invitation to a new team member. They'll receive login credentials via email.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="invite-name">Name <span className="text-destructive">*</span></Label>
              <Input
                id="invite-name"
                placeholder="John Doe"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                data-testid="invite-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-email">Email <span className="text-destructive">*</span></Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="john@company.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invite-role">Role <span className="text-destructive">*</span></Label>
              <Select
                value={inviteForm.role}
                onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={() => setQuickInviteOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleQuickInvite} disabled={isInviting} data-testid="send-invite-btn">
              {isInviting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                'Send Invite'
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Password Display Dialog (when email fails) */}
      <AlertDialog open={showInvitePassword} onOpenChange={(open) => {
        if (!open) {
          setShowInvitePassword(false);
          setInvitePassword('');
          setQuickInviteOpen(false);
          setInviteForm({ name: '', email: '', department: '', role: 'user' });
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Temporary Password</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Email delivery failed. Please share this password manually with the new user:</p>
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg font-mono text-lg">
                {invitePassword}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(invitePassword);
                    toast.success('Password copied to clipboard');
                  }}
                >
                  Copy
                </Button>
              </div>
              <p className="text-sm text-muted-foreground">
                The user should change this password after their first login.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setShowInvitePassword(false);
              setInvitePassword('');
              setQuickInviteOpen(false);
              setInviteForm({ name: '', email: '', department: '', role: 'user' });
            }}>
              Done
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
